import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface Vehiculo {
  id: string;
  tipo: 'compra' | 'moto' | 'alquiler';
  nombre: string;
  precio: number;
  precio_oferta?: number;
  oferta_activa?: boolean;
  precio_dia?: number;
  descripcion: string;
  imagen: string;
  imagenes?: string[];
  km?: number;
  anio?: number;
  combustible?: string;
  disponible: boolean;
  potencia?: number;
  transmision?: string;
  color?: string;
  puertas?: number;
  plazas?: number;
}

export interface TeamMember {
  id: string;
  nombre: string;
  rol: string;
  experiencia: string;
  imagen: string;
}

export interface Settings {
  nombre_taller: string;
  telefono: string;
  whatsapp: string;
  email: string;
  direccion: string;
  ciudad: string;
  horario_semana: string;
  horario_sabado: string;
  servicios: Array<{
    nombre: string;
    precio: string;
    oferta?: string;
    oferta_activa?: boolean;
    imagen?: string;
  }>;
  vehiculos: Vehiculo[];
  equipo: TeamMember[];
}

const defaultSettings: Settings = {
  nombre_taller: 'Taller Mecánico Kevin Alberto',
  telefono: '+34 666 36 59 22',
  whatsapp: 'https://wa.me/34666365922',
  email: 'tallerkevinalberto@gmail.com',
  direccion: 'C. Guadalimar, 7, 29400 Ronda, Málaga',
  ciudad: 'Ronda',
  horario_semana: 'Lun – Vie: 07:00 – 15:00',
  horario_sabado: 'Sáb – Dom: Cerrado',
  servicios: [
    { nombre: 'Revisión periódica', precio: 'Desde 49 €' },
    { nombre: 'Diagnóstico electrónico', precio: 'Desde 29 €' },
    { nombre: 'Frenos y suspensión', precio: 'Desde 79 €' },
    { nombre: 'Chapa y pintura', precio: 'Presupuesto' },
    { nombre: 'Climatización', precio: 'Desde 59 €' },
  ],
  vehiculos: [],
  equipo: [],
};

export function useSettings() {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: rows } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('key, value')
        .in('key', ['taller_info', 'horarios', 'servicios', 'vehiculos', 'equipo']);

      if (!rows || rows.length === 0) {
        setSettings(defaultSettings);
        return;
      }

      const map: Record<string, unknown> = {};
      for (const row of rows) {
        map[row.key] = row.value;
      }

      const tallerInfo = (map['taller_info'] as Record<string, string> | null) ?? {};
      const horarios = (map['horarios'] as Record<string, string> | null) ?? {};
      const servicios = (map['servicios'] as Settings['servicios'] | null) ?? defaultSettings.servicios;
      const vehiculos = (map['vehiculos'] as Vehiculo[] | null) ?? defaultSettings.vehiculos;
      const equipo = (map['equipo'] as TeamMember[] | null) ?? defaultSettings.equipo;

      setSettings({
        nombre_taller: tallerInfo.nombre_taller ?? defaultSettings.nombre_taller,
        telefono: tallerInfo.telefono ?? defaultSettings.telefono,
        whatsapp: tallerInfo.whatsapp ?? defaultSettings.whatsapp,
        email: tallerInfo.email ?? defaultSettings.email,
        direccion: tallerInfo.direccion ?? defaultSettings.direccion,
        ciudad: tallerInfo.ciudad ?? defaultSettings.ciudad,
        horario_semana: horarios.lunes_viernes ?? defaultSettings.horario_semana,
        horario_sabado: horarios.sabado ?? defaultSettings.horario_sabado,
        servicios,
        vehiculos,
        equipo,
      });
    } catch {
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { settings, loading, reload: load };
}
